import { useState, useEffect } from 'react';
import { ReactFCWithChildren } from 'react';
import { OverlayTrigger, Tooltip, Modal } from 'react-bootstrap';
import { Formik, Form, Field } from 'formik';
import * as yup from 'yup';
import ApiClient from 'src/lib/api_client';
import useEnhanceApiClient from 'src/hooks/useEnhanceApiClient';
import { AppRoutes } from 'src/lib/routes';
import WhatsappSvg from 'public/images/svg_icons/whatsapp.svg';
import FacebookSvg from 'public/images/svg_icons/facebook.svg';
import Copyable from 'src/components/Copyable';
import CopySvg from 'public/images/svg_icons/copy.svg';
import Loading from 'src/components/Loading';
import Money from 'src/components/Money';
import { FiX, FiInfo } from 'react-icons/fi';
import { isEnvProduction, isEnvStaging } from 'src/helpers';
import { TMoney } from 'src/types';
import BadgeSvg from 'public/images/svg_icons/badge.svg';
import useCurrentUser from 'src/hooks/useCurrentUser';
import { useTranslations, useLocale } from 'next-intl';
import ErrorMesseageTranslation from 'src/components/ErrorMesseageTranslation';

const TOP_LEVEL_DOMAIN = isEnvProduction()
  ? 'seeksophie.com'
  : isEnvStaging()
  ? 'seeksotest.org'
  : 'localhost:4000';
const ValidationSchema = yup.object({
  inputUrl: yup
    .string()
    .matches(new RegExp(`^(https?:\/\/)?(www.)?${TOP_LEVEL_DOMAIN}(\/|$)`), 'validationUrlInvalid')
    .required('validationUrlInvalid')
    .trim(),
});

type Props = {
  url?: string;
  shareUrl?: string;
  shareable?: { gid: string };
  estimatedAmount?: TMoney;
  maxEstimatedAmount?: TMoney;
  title?: string;
  note?: string;
};

const ShareUrl: React.FC<{ affiliateUrl: string }> = ({ affiliateUrl }) => {
  return (
    <div className="tw-flex tw-justify-center tw-items-center">
      <div className="tw-flex-grow tw-bg-white tw-border tw-border-grey-lighter tw-rounded-4xl tw-px-3 tw-py-2.5 tw-flex tw-items-center tw-justify-between">
        <input
          readOnly
          value={affiliateUrl}
          type="text"
          className="tw-appearance-none tw-outline-none tw-flex-grow tw-text-brand-navy tw-placeholder-brand-navy"
        />
        <Copyable as="div" className="tw-cursor-pointer" copyContent={affiliateUrl}>
          <CopySvg width={24} height={24} className="tw-text-orange tw-ml-2" />
        </Copyable>
      </div>
      <div className="tw-ml-4 tw-flex-shrink-0">
        <a
          href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(affiliateUrl)}`}
          target="_blank"
          className="tw-text-brand-navy"
          rel="noreferrer noopener"
        >
          <FacebookSvg width={20} height={20} className="tw-cursor-pointer tw-mr-3" />
        </a>
        <a
          href={`https://api.whatsapp.com/send?text=${encodeURIComponent(affiliateUrl)}`}
          target="_blank"
          className="tw-text-brand-navy"
          rel="noreferrer noopener"
        >
          <WhatsappSvg width={20} height={20} className="tw-cursor-pointer" />
        </a>
      </div>
    </div>
  );
};

const AffiliateLinkButton: ReactFCWithChildren<Props> = ({
  children,
  url,
  shareUrl,
  shareable,
  estimatedAmount,
  maxEstimatedAmount,
  title,
  note
}) => {
  const [show, setShow] = useState(false);
  const [copied, setCopied] = useState(false);
  const showUrlInput = !url;
  const createAffiliateLink = useEnhanceApiClient(ApiClient.createAffiliateLink);
  const [affiliateUrl, setAffiliateUrl] = useState(shareUrl);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState('');
  const [currentUser] = useCurrentUser();
  const isNormalUser = currentUser ? currentUser?.ambassadorProfile?.status !== 'active' : true
  const translation = useTranslations('components.ambassador.AffiliateLinkButton')

  useEffect(() => {
    if (currentUser?.id && !isNormalUser && show && !affiliateUrl && !!url) {
      setIsGenerating(true);
      createAffiliateLink({
        input_url: url,
        shareable_gid: shareable?.gid,
      })
        .then((result) => {
          const [json] = result;
          if (!('error' in json)) {
            setAffiliateUrl(AppRoutes.affiliateRoute({ id: json.code }).toUrl());
          }
        })
        .finally(() => {
          setIsGenerating(false);
        });
    }
  }, [show, url, affiliateUrl, isNormalUser, currentUser?.id]);

  return (
    <>
      <div
        onClick={() => {
          setShow(true);
        }}
      >
        {children}
      </div>
      <Formik
        initialValues={{ inputUrl: '' }}
        validationSchema={ValidationSchema}
        onSubmit={(values, { setSubmitting }) => {
          setIsGenerating(true);
          createAffiliateLink(values)
            .then((res) => {
              const [json] = res;
              if ('error' in json) {
                setError(json.error);
              } else {
                setAffiliateUrl(AppRoutes.affiliateRoute({ id: json.code }).toUrl());
              }
            })
            .finally(() => {
              setSubmitting(false);
              setIsGenerating(false);
            });
        }}
      >
        {(formik) => (
          <Modal
            show={show}
            onHide={() => {
              setShow(false);
              formik.resetForm();
              setAffiliateUrl(undefined);
            }}
            className="modal-styled modal-styled-sm"
            backdrop="static"
          >
            <Modal.Header>
              <div className="tw-flex tw-justify-between tw-w-full">
                {showUrlInput ? (
                  <div>
                    <div className="tw-text-xl md:tw-text-2xl tw-font-semibold tw-font-styleSans tw-leading-none md:tw-leading-none">
                      {translation('generateSharingLink')}
                    </div>
                    <div>{translation('createYourReferralLink')}</div>
                  </div>
                ) : (
                  <div className="tw-text-xl md:tw-text-2xl tw-font-semibold tw-font-styleSans tw-leading-none md:tw-leading-none">
                    {title || translation('shareThisExperience')}
                  </div>
                )}
                <div
                  className="tw-ml-4 tw-flex-shrink-0 tw-cursor-pointer"
                  onClick={() => setShow(false)}
                >
                  <FiX size={20} />
                </div>
              </div>
            </Modal.Header>
            <Modal.Body>
              {showUrlInput ? (
                <>
                  <div className="form-group">
                    <div className="tw-mb-2">
                      Seek Sophie {translation('url')}
                      <OverlayTrigger
                        placement="top"
                        overlay={
                          <Tooltip id="test-3">
                            {translation('forEg')}.
                            https://www.seeksophie.com/collections/labuan-bajo-komodo-komodo-national-park-experiences-c32jlpolz6
                          </Tooltip>
                        }
                      >
                        <FiInfo className="tw-text-brand-navy tw-ml-1 tw-mb-0.5" size={16} />
                      </OverlayTrigger>
                    </div>
                    <Field name="inputUrl" className="form-control" />
                    <ErrorMesseageTranslation name="inputUrl" />
                    {error ? <div className="tw-block invalid-feedback">{error}</div> : null}
                  </div>

                  {isGenerating ? (
                    <Loading className="tw-justify-center tw-mt-6" />
                  ) : affiliateUrl ? (
                    <div className="tw-bg-brand-green tw-p-4 tw-rounded-lg tw-mt-6">
                      <div className="tw-text-center tw-font-semibold tw-mb-2">
                        {translation('yourReferralLink')}!
                      </div>
                      <div className="tw-text-center tw-mb-2 tw-text-sm">
                        {translation('shareThisLinkToStartEarning')}!
                      </div>
                      <ShareUrl affiliateUrl={affiliateUrl} />
                    </div>
                  ) : null}
                </>
              ) : (
                <>
                  {isNormalUser ? (
                    <div>
                      <div className="tw-mb-2">{translation('copyAndShareThisLink')}:</div>
                      <ShareUrl affiliateUrl={url} />
                    </div>
                  ) : affiliateUrl ? (
                    <div>
                      <div className="tw-mb-2">{translation('copyAndShareThisLink')}:</div>
                      <ShareUrl affiliateUrl={affiliateUrl} />

                      {estimatedAmount ? (
                        <>
                          <div className="tw-my-4 tw-border-t tw-border-grey-lighter"></div>

                          <div className="tw-flex tw-items-center tw-mb-4">
                            <div className="tw-relative tw-mr-3">
                              <BadgeSvg className="tw-h-14 tw-w-14" />
                              <div className="tw-absolute tw-font-medium tw-text-brand-green tw-leading-tight tw-text-xs tw-w-full tw-text-center tw-top-5">
                                {translation('earn')}
                              </div>
                            </div>
                            <div>
                              <div className="tw-mb-1 tw-leading-none">{translation('estimatedEarnings')}:</div>
                              <div className="tw-font-semibold tw-text-orange">
                                {translation('from')}{' '}
                                <Money
                                  amount={estimatedAmount}
                                  className=""
                                />
                                {maxEstimatedAmount && Number(maxEstimatedAmount.amount) > Number(estimatedAmount.amount) ? (
                                  <>
                                    <span className="tw-mx-1">-</span>
                                    <Money
                                      amount={maxEstimatedAmount}
                                      className=""
                                    />
                                  </>
                                ): null}
                              </div>
                            </div>
                          </div>

                          <div className="tw-text-grey">
                            {translation('yourEarningWillBe')}
                          </div>
                        </>
                      ): null}

                      {note ? (
                        <>
                          <div className="tw-my-4 tw-border-t tw-border-grey-lighter"></div>
                          <div className="tw-tex-sm">
                            {note}
                          </div>
                        </>
                      ) : null}
                    </div>
                  ) : isGenerating ? (
                    <Loading className="tw-justify-center" />
                  ) : null}
                </>
              )}
            </Modal.Body>

            {showUrlInput ? (
              <Modal.Footer>
                <Form className="tw-m-0">
                  {affiliateUrl ? (
                    <div className="tw-flex tw-items-center">
                      <button
                        type="button"
                        className="btn btn-outline tw-bg-white"
                        onClick={() => {
                          formik.resetForm();
                          setAffiliateUrl(undefined);
                          setCopied(false);
                        }}
                      >
                        {translation('generateOtherLink')}
                      </button>
                      <Copyable
                        as="div"
                        className="tw-cursor-pointer tw-ml-2"
                        copyContent={affiliateUrl}
                        onCopy={() => setCopied(true)}
                      >
                        <button type="button" className="btn btn-primary">
                          {copied ? translation('copied') : translation('copy')}
                        </button>
                      </Copyable>
                    </div>
                  ) : (
                    <div className="">
                      <button
                        type="button"
                        className="btn btn-outline tw-bg-white"
                        onClick={() => setShow(false)}
                      >
                        {translation('close')}
                      </button>
                      <button
                        type="submit"
                        className="btn btn-primary tw-ml-2"
                        disabled={formik.isSubmitting}
                      >
                        {translation('generateShareLink')}
                      </button>
                    </div>
                  )}
                </Form>
              </Modal.Footer>
            ) : null}
          </Modal>
        )}
      </Formik>
    </>
  );
};

export default AffiliateLinkButton;
